import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import Editor from '@monaco-editor/react';

const SplitViewEditor = forwardRef(({ file, onContentChange, onSave }, ref) => {
  const [code, setCode] = useState(file?.content || '');
  const [viewMode, setViewMode] = useState('code'); // code, split, preview
  const [theme, setTheme] = useState('vs-dark');
  const [fontSize, setFontSize] = useState(14);
  const [previewContent, setPreviewContent] = useState('');
  const iframeRef = useRef(null);
  const editorRef = useRef(null);

  useImperativeHandle(ref, () => ({
    format: () => {
      if (editorRef.current) {
        editorRef.current.getAction('editor.action.formatDocument').run();
      }
    },
    triggerAction: (actionId) => {
      if (editorRef.current) {
        editorRef.current.trigger(null, actionId);
      }
    },
    insertText: (text) => {
      if (editorRef.current) {
        const selection = editorRef.current.getSelection();
        const id = { major: 1, minor: 1 };
        const op = { range: selection, text: text, forceMoveMarkers: true };
        editorRef.current.executeEdits("my-source", [op]);
      }
    }
  }));

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
  };

  useEffect(() => {
    if (file) {
      setCode(file.content || '');
      updatePreview(file.content || '');
    }
  }, [file?.id]);

  const handleEditorChange = (value) => {
    setCode(value);
    if (onContentChange) {
      onContentChange(value);
    }
    if (viewMode !== 'code') {
      updatePreview(value);
    }
  };

  const updatePreview = (content) => {
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'html') {
      setPreviewContent(content);
    } else if (ext === 'md' || ext === 'markdown') {
      // Simple markdown to HTML (in production, use a proper markdown parser)
      const html = content
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
        .replace(/\*(.*)\*/gim, '<em>$1</em>')
        .replace(/\n/gim, '<br/>');
      setPreviewContent(`
        <html>
          <head>
            <style>
              body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; }
              h1 { color: #2563eb; }
              h2 { color: #3b82f6; }
              h3 { color: #60a5fa; }
            </style>
          </head>
          <body>${html}</body>
        </html>
      `);
    } else if (ext === 'jsx' || ext === 'js') {
      // For React components, show a placeholder
      setPreviewContent(`
        <html>
          <head>
            <style>
              body {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100vh;
                font-family: sans-serif;
                background: #1f2937;
                color: white;
              }
              .message { text-align: center; }
            </style>
          </head>
          <body>
            <div class="message">
              <h2>⚛️ React Component</h2>
              <p>Live preview available when running dev server</p>
            </div>
          </body>
        </html>
      `);
    }
  };

  const getLanguage = () => {
    if (!file) return 'javascript';
    const ext = file.name.split('.').pop().toLowerCase();
    const langMap = {
      js: 'javascript',
      jsx: 'javascript',
      ts: 'typescript',
      tsx: 'typescript',
      py: 'python',
      html: 'html',
      css: 'css',
      json: 'json',
      md: 'markdown',
      txt: 'plaintext'
    };
    return langMap[ext] || 'plaintext';
  };

  const canPreview = () => {
    if (!file) return false;
    const ext = file.name.split('.').pop().toLowerCase();
    return ['html', 'md', 'markdown', 'jsx', 'js'].includes(ext);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(code);
    }
  };

  // Use a ref to store the latest save handler to avoid re-adding event listener
  const saveHandlerRef = useRef(handleSave);
  useEffect(() => {
    saveHandlerRef.current = handleSave;
  }, [code]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveHandlerRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!file) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900 text-gray-500">
        <div className="text-center">
          <svg className="w-24 h-24 mx-auto mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-lg">No file selected</p>
          <p className="text-sm mt-2">Select a file from the sidebar to start editing</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-900">
      {/* Toolbar */}
      <div className="h-12 bg-gray-950 border-b border-gray-700 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          {/* View Mode */}
          <div className="flex items-center gap-1 bg-gray-800 rounded p-1">
            <button
              onClick={() => setViewMode('code')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                viewMode === 'code' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Code
            </button>
            {canPreview() && (
              <>
                <button
                  onClick={() => setViewMode('split')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode === 'split' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Split
                </button>
                <button
                  onClick={() => {
                    setViewMode('preview');
                    updatePreview(code);
                  }}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode === 'preview' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Preview
                </button>
              </>
            )}
          </div>

          {/* File Info */}
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>{getLanguage()}</span>
            <span>•</span>
            <span>{code.split('\n').length} lines</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Font Size */}
          <select
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300"
          >
            <option value={12}>12px</option>
            <option value={14}>14px</option>
            <option value={16}>16px</option>
            <option value={18}>18px</option>
            <option value={20}>20px</option>
          </select>

          {/* Theme */}
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300"
          >
            <option value="vs-dark">Dark</option>
            <option value="light">Light</option>
            <option value="hc-black">High Contrast</option>
          </select>
        </div>
      </div>

      {/* Editor/Preview Area */}
      <div className="flex-1 flex">
        {/* Code Editor */}
        {(viewMode === 'code' || viewMode === 'split') && (
          <div className={viewMode === 'split' ? 'flex-1 border-r border-gray-700' : 'flex-1'}>
            <Editor
              height="100%"
              language={getLanguage()}
              value={code}
              onChange={handleEditorChange}
              onMount={handleEditorDidMount}
              theme={theme}
              options={{
                fontSize: fontSize,
                minimap: { enabled: true },
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                automaticLayout: true,
                tabSize: 2,
                formatOnPaste: true,
                formatOnType: true,
                suggestOnTriggerCharacters: true,
                quickSuggestions: true,
                folding: true,
                lineNumbers: 'on',
                rulers: [80, 120],
                bracketPairColorization: {
                  enabled: true
                }
              }}
            />
          </div>
        )}

        {/* Preview */}
        {(viewMode === 'preview' || viewMode === 'split') && canPreview() && (
          <div className={viewMode === 'split' ? 'flex-1' : 'flex-1'}>
            <iframe
              ref={iframeRef}
              srcDoc={previewContent}
              title="Preview"
              className="w-full h-full bg-white"
              sandbox="allow-scripts"
            />
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="h-6 bg-blue-600 px-4 flex items-center justify-between text-xs text-white">
        <div className="flex items-center gap-4">
          <span>Ln {code.split('\n').length}, Col 1</span>
          <span>UTF-8</span>
          <span>{getLanguage().toUpperCase()}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="opacity-75">Ctrl+S to save</span>
        </div>
      </div>
    </div>
  );
});

export default SplitViewEditor;
